import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Container, Card } from 'reactstrap';
import firebase from '../../config/firebase.config';
import PulseLoader from 'react-spinners/PulseLoader';
import { Spinner } from 'reactstrap';

import { getTeamMemberDetails } from '../../services/teamMemberDetails';
import { Table } from '@/components/Table';
import { columnsConfig } from './Leave.config';
import styles from './Leave.module.scss';
import { ModalComponent } from '@/components/Modal/Modal.component';
import { LeaveFormModal } from './LeaveFormModal.component';
import { UpdateLeaveFormModal } from './UpdateLeaveFormModal.component';
import { leaveBalanceData } from './Leave.constants';
import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { leaveBalanceService } from '@/services/leaveBalance';
import { Button, buttonVariants } from '@/components/Button';
const database = firebase.firestore();

const MANAGER_ROLES = ['Manager', 'Admin'];

export const LeaveComponent = () => {
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { profile } = useSelector((state) => state.auth);
  const rowDataRef = useRef(null);
  const [showAddLeaveFormModal, setShowAddLeaveFormModal] = useState(false);
  const [showUpdateLeaveModal, setShowUpdateLeaveModal] = useState(false);
  const [leaveRequestsList, setLeaveRequestsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leaveData, setLeaveData] = useState({
    loading: true,
    totalAllocatedLeaves: 0,
    usedLeaves: 0,
    availableLeaves: 0,
    pendingRequests: 0,
  });
  const [companyManagers, setCompanyManagers] = useState([]);

  // Company leave: single source — companyDatabase/{companyId}/leaveRequests
  const fetchLeaveRequestsList = () => {
    if (!activeCompany?.companyId) return;
    const data = [];
    setLoading(true);
    database
      .collection('companyDatabase')
      .doc(activeCompany.companyId)
      .collection('leaveRequests')
      .where('userId', '==', profile.id)
      .get()
      .then((res) => {
        res.docs.forEach((docSnap) => {
          data.push({ ...docSnap.data(), id: docSnap.id });
        });
        setLeaveRequestsList(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  const fetchLeaveBalance = async () => {
    try {
      const balance = await leaveBalanceService.getEffectiveRemainingPaidLeave(
        profile.id,
        activeCompany?.companyId
      );
      setLeaveData((prev) => ({
        loading: false,
        totalAllocatedLeaves: balance.totalAllocated,
        usedLeaves: balance.used,
        availableLeaves: balance.effectiveRemaining,
        pendingRequests: balance.pendingRequests,
      }));
    } catch (error) {
      console.log(error, '>>> Leave.component.jsx <<<', 'line-74');
    } finally {
      setLeaveData((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };
  useEffect(() => {
    fetchLeaveBalance();
  }, []);

  // Load company-wide managers via getTeamMemberDetails (supports legacy + sharded)
  useEffect(() => {
    if (!activeCompany?.companyId) return;
    database
      .collection('teamDatabase')
      .where('ownedBy', '==', activeCompany.companyId)
      .get()
      .then(async (snap) => {
        const teamIds = snap.docs.map((d) => d.id);
        const byUid = {};
        await Promise.all(
          teamIds.map(async (teamId) => {
            const memberDetails = await getTeamMemberDetails(database, teamId);
            Object.entries(memberDetails || {}).forEach(
              ([userUID, details]) => {
                const role = details.userType || details.role || '';
                if (MANAGER_ROLES.includes(role)) {
                  byUid[userUID] = {
                    userUID,
                    userName:
                      details.userName || details.accountFullName || userUID,
                  };
                }
              }
            );
          })
        );
        setCompanyManagers(Object.values(byUid));
      })
      .catch(() => setCompanyManagers([]));
  }, [activeCompany?.companyId]);

  useEffect(() => {
    if (activeCompany?.companyId) fetchLeaveRequestsList();
  }, [activeCompany?.companyId]);

  const onRowClick = (data) => {
    if (data?.status !== 'Declined') {
      setShowUpdateLeaveModal(true);
      rowDataRef.current = data;
    }
  };
  const onCloseAddLeaveFormModal = () => {
    setShowAddLeaveFormModal(false);
  };
  const onCloseUpdateLeaveModal = () => {
    setShowUpdateLeaveModal(false);
  };
  return (
    <>
      <div className={styles.leavePage}>
        <Button
          variant={buttonVariants.primary_rounded}
          text="+ Add Leave Request"
          onClick={() => setShowAddLeaveFormModal(true)}
          disabled={leaveData.loading}
        />
        <div className={styles.leaveDataContainer}>
          {leaveData.loading ? (
            <Spinner color="#4f58bb" style={{ marginLeft: '12px' }} />
          ) : (
            <>
              {' '}
              {leaveBalanceData.map((item) => {
                return (
                  <div className={styles.leaveDataItem}>
                    <Text
                      variant={textVariantsEnum.Text_xs}
                      fontWeight={fontWeightEnum.Normal}
                    >
                      {item.label.toUpperCase()}
                    </Text>

                    <Text
                      variant={textVariantsEnum.Text_lg}
                      fontWeight={fontWeightEnum.Bold}
                    >
                      {leaveData[item.key]}
                    </Text>
                  </div>
                );
              })}
            </>
          )}
        </div>
        {loading ? (
          <div className="w-100 d-flex justify-content-center mt-2">
            <PulseLoader color="#4f58bb" />
          </div>
        ) : (
          <Table
            className={styles.leaveTableContainer}
            data={leaveRequestsList}
            columns={columnsConfig({
              companyManagers,
            })}
            onRowClick={onRowClick}
          />
        )}
      </div>

      {/* Modal for creating leave request */}
      <ModalComponent
        isVisible={showAddLeaveFormModal}
        onClose={onCloseAddLeaveFormModal}
      >
        <LeaveFormModal
          companyManagers={companyManagers}
          onClose={onCloseAddLeaveFormModal}
          fetchLeaveRequestsList={fetchLeaveRequestsList}
          fetchLeaveBalance={fetchLeaveBalance}
          leaveData={leaveData}
        />
      </ModalComponent>
      <ModalComponent
        isVisible={showUpdateLeaveModal}
        onClose={onCloseUpdateLeaveModal}
      >
        <UpdateLeaveFormModal
          leaveData={rowDataRef.current}
          companyManagers={companyManagers}
          onClose={onCloseUpdateLeaveModal}
          fetchLeaveRequestsList={fetchLeaveRequestsList}
        />
      </ModalComponent>
    </>
  );
};
